/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================
*/

import { useState, useEffect, Fragment, useMemo } from "react";
import Collapse from "@mui/material/Collapse";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";

// Soft UI Dashboard React context
import { useSoftUIController, setMiniSidenav } from "context";

// ✅ Auth (para leer user.permissions)
import { useAuth } from "layouts/auth/AuthContext";

/** Mapea módulos del FRONT (routes.js) a módulos guardados en backend (permissions) */
const MODULE_MAP_UI_TO_API = {
  usuarios: "user",
  reconocimientos: "award",
  reportes: "reportes",
  dashboard: "dashboard",
  sellos: "sellos",
  generos: "generos",
  grupos: "grupos",
  nacionalidades: "nacionalidades",
  certificaciones: "certificaciones",
  parametros: "parametros",
  formatos: "formatos",
  grabacion: "grabacion",
};

/** Reglas:
 *  - Admin: todo
 *  - read: true si read OR (create/update/delete)
 *  - create/update/delete: exacto
 */
function hasPermission(user, uiModule, action = "read") {
  if (!user) return false;

  const isAdmin =
    user?.is_admin === true ||
    user?.is_admin === 1 ||
    String(user?.is_admin) === "1";

  if (isAdmin) return true;

  const apiModule = MODULE_MAP_UI_TO_API[uiModule] || uiModule;
  
  const perms = user?.permissions || {};
  const p = perms?.[apiModule];

  if (!p) return false;

  if (action === "read") {
    return !!p.read || !!p.create || !!p.update || !!p.delete;
  }
  return !!p[action];
}

/** Filtra recursivamente las rutas por permisos (respeta collapse) */
function filterRoutesByPermissions(routes, user) {
  return (routes || [])
    .map((r) => {
      // 1) Si tiene auth, validar
      if (r?.auth?.module && r?.auth?.action) {
        if (!hasPermission(user, r.auth.module, r.auth.action)) return null;
      }

      // 2) Si tiene hijos, filtrar hijos (y esconder grupo si queda vacío)
      if (Array.isArray(r?.collapse)) {
        const filteredChildren = filterRoutesByPermissions(r.collapse, user);
        if (!filteredChildren.length) return null;
        return { ...r, collapse: filteredChildren };
      }

      return r;
    })
    .filter(Boolean);
}

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;

  const { user } = useAuth();
  const [openGroups, setOpenGroups] = useState({});
  const isActiveExact = (route) => pathname === route;

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // ✅ Solo renderiza rutas permitidas
  const allowedRoutes = useMemo(
    () => filterRoutesByPermissions(routes, user),
    [routes, user]
  );

  // Render all the routes from the routes.js (visible items on the Sidenav)
  const renderRoutes = allowedRoutes.map((item) => {
    const { type, name, icon, noCollapse, key, route, href, collapse } = item;

    // 1) GRUPO con hijos
    if (type === "collapse" && Array.isArray(collapse) && !route && !href) {
      const anyChildActive = collapse.some((c) => c.route && isActiveExact(c.route));
      const isOpen = openGroups[key] ?? anyChildActive;
      const toggle = () => setOpenGroups((s) => ({ ...s, [key]: !isOpen }));

      return (
        <Fragment key={key}>
          <div onClick={toggle} style={{ cursor: "pointer" }}>
            <SidenavCollapse
              color={color}
              name={name}
              icon={icon}
              active={false}
              noCollapse={false}
              isGroup
              open={isOpen}
            />
          </div>

          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <SoftBox pl={3} mt={0.5} mb={1}>
              {collapse.map((child) => {
                if (child.type !== "collapse" || (!child.route && !child.href)) return null;
                const childActive = child.route ? isActiveExact(child.route) : false;

                return child.href ? (
                  <Link
                    href={child.href}
                    key={child.key}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ textDecoration: "none" }}
                  >
                    <SidenavCollapse
                      color={color}
                      name={child.name}
                      icon={child.icon}
                      active={childActive}
                      noCollapse
                    />
                  </Link>
                ) : (
                  <NavLink to={child.route} end key={child.key}>
                    <SidenavCollapse
                      color={color}
                      name={child.name}
                      icon={child.icon}
                      active={childActive}
                      noCollapse
                    />
                  </NavLink>
                );
              })}
            </SoftBox>
          </Collapse>
        </Fragment>
      );
    }

    // 2) ÍTEM simple
    if (type === "collapse" && route && !Array.isArray(collapse)) {
      const isActive = isActiveExact(route);

      return href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            color={color}
            name={name}
            icon={icon}
            active={isActive}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink to={route} end key={key}>
          <SidenavCollapse
            color={color}
            name={name}
            icon={icon}
            active={isActive}
            noCollapse={noCollapse}
          />
        </NavLink>
      );
    }

    // 3) Títulos / separadores
    if (type === "title") {
      return (
        <SoftTypography
          key={key}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {item.title}
        </SoftTypography>
      );
    }
    if (type === "divider") return <Divider key={key} />;

    return null;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>

        <SoftBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <SoftBox component="img" src={brand} alt={brandName} width="10rem" />}
        </SoftBox>
      </SoftBox>

      <Divider />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
